import { Link } from 'react-router-dom';
import { Shield, Package, Truck, RotateCcw, HeadphonesIcon, Wrench, CreditCard, CheckCircle2, Clock, MapPin, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbSeparator, BreadcrumbPage } from '../components/ui/breadcrumb';
import SEO from '../components/SEO';

const GarantiesEngagementsPage = () => {
  const engagements = [
    {
      icon: CreditCard,
      title: 'Paiement 100% sécurisé',
      color: 'text-green-600',
      bgColor: 'bg-green-50',
      borderColor: 'border-green-200',
      content: [
        'Paiement par carte bancaire via Stripe, leader mondial du paiement en ligne',
        'Vos données bancaires ne transitent jamais par nos serveurs',
        'Cryptage SSL et protocoles de sécurité certifiés',
        'Confirmation de paiement immédiate par email'
      ]
    },
    {
      icon: Package,
      title: 'Livraison France entière',
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200',
      content: [
        'Pièces détachées, accessoires et produits d\'entretien livrés partout en France métropolitaine',
        'Emballage soigné et adapté à chaque produit',
        'Suivi de colis disponible',
        'Livraison offerte dès 80 € TTC d\'achat'
      ]
    },
    {
      icon: Clock,
      title: 'Délais d\'expédition clairs',
      color: 'text-orange-600',
      bgColor: 'bg-orange-50',
      borderColor: 'border-orange-200',
      content: [
        'Expédition sous 24 à 72h pour les produits en stock',
        'Délais spécifiques indiqués sur chaque fiche produit',
        'Préparation soignée de chaque commande',
        'Notification par email à chaque étape'
      ]
    },
    {
      icon: RotateCcw,
      title: 'Droit de rétractation',
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
      borderColor: 'border-purple-200',
      content: [
        '14 jours pour changer d\'avis conformément à la législation',
        'Produit neuf, non utilisé et dans son emballage d\'origine',
        'Frais de retour à la charge du client',
        'Remboursement sous 14 jours après réception du retour'
      ],
      note: 'Les produits sur mesure, coupés ou personnalisés ne sont pas éligibles au droit de rétractation.'
    },
    {
      icon: HeadphonesIcon,
      title: 'Accompagnement technique',
      color: 'text-cyan-600',
      bgColor: 'bg-cyan-50',
      borderColor: 'border-cyan-200',
      content: [
        'Conseils avant achat pour vous aider à choisir',
        'Informations techniques sur la compatibilité des pièces',
        'Réponse par email sous 24 à 48h ouvrées',
        'Documentation et guides disponibles sur le site'
      ]
    },
    {
      icon: Wrench,
      title: 'Installation & SAV des spas',
      color: 'text-slate-600',
      bgColor: 'bg-slate-50',
      borderColor: 'border-slate-200',
      content: [
        'Installation et mise en service assurées par nos équipes',
        'Service après-vente réactif et professionnel',
        'Interventions techniques par des spécialistes du spa',
        'Suivi personnalisé de votre équipement'
      ],
      note: 'Zone d\'intervention : Loire-Atlantique (44), Vendée (85), Morbihan (56), Maine-et-Loire (49).'
    }
  ];

  const trustPoints = [
    { icon: Shield, text: 'Boutique spécialisée spa depuis plusieurs années' },
    { icon: CheckCircle2, text: 'Pièces neuves et d\'origine' },
    { icon: MapPin, text: 'Entreprise française basée dans le Grand Ouest' },
    { icon: Mail, text: 'Contact humain et réactif par email' }
  ];

  return (
    <>
      <SEO 
        title="Garanties & Engagements | Atlantique Spa Boutique"
        description="Découvrez nos garanties : paiement sécurisé Stripe, livraison France entière, droit de rétractation 14 jours, accompagnement technique. Achetez en confiance."
      />
      
      <div className="min-h-screen bg-gray-50">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-[#1E3A5F] via-[#2d4a6f] to-[#0F172A] text-white py-16">
          <div className="container mx-auto px-4">
            {/* Breadcrumb */}
            <Breadcrumb className="mb-8">
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link to="/" className="text-white/70 hover:text-white">Accueil</Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator className="text-white/50" />
                <BreadcrumbItem>
                  <BreadcrumbPage className="text-white">Garanties & Engagements</BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
            
            <div className="max-w-3xl">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-3 bg-white/10 rounded-xl">
                  <Shield className="w-8 h-8 text-orange-400" />
                </div>
                <span className="text-orange-400 font-medium uppercase tracking-wider text-sm">
                  Nos engagements
                </span>
              </div>
              
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
                Nos garanties & engagements
                <span className="block text-orange-400 mt-2">Acheter en toute confiance</span>
              </h1>
              
              <p className="text-lg text-white/80 leading-relaxed">
                Chez Atlantique Spa Boutique, nous mettons tout en œuvre pour vous offrir 
                une expérience d'achat sereine et transparente. Découvrez nos engagements 
                pour vous accompagner avant, pendant et après votre commande.
              </p>
            </div>
          </div>
        </section>

        {/* Trust Points Bar */}
        <section className="bg-white border-b py-6">
          <div className="container mx-auto px-4">
            <div className="flex flex-wrap justify-center gap-6 md:gap-12">
              {trustPoints.map((point, index) => (
                <div key={index} className="flex items-center gap-2 text-gray-700">
                  <point.icon className="w-5 h-5 text-[#1E3A5F]" />
                  <span className="text-sm font-medium">{point.text}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Main Content */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="grid gap-8 lg:grid-cols-2">
              {engagements.map((engagement, index) => (
                <div 
                  key={index}
                  className={`${engagement.bgColor} ${engagement.borderColor} border rounded-2xl p-8 transition-all hover:shadow-lg`}
                >
                  <div className="flex items-start gap-4 mb-6">
                    <div className={`p-3 bg-white rounded-xl shadow-sm`}>
                      <engagement.icon className={`w-7 h-7 ${engagement.color}`} />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-gray-900">{engagement.title}</h2>
                    </div>
                  </div>
                  
                  <ul className="space-y-3">
                    {engagement.content.map((item, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <CheckCircle2 className={`w-5 h-5 ${engagement.color} shrink-0 mt-0.5`} />
                        <span className="text-gray-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                  
                  {engagement.note && (
                    <div className="mt-4 p-3 bg-white/60 rounded-lg border border-gray-200">
                      <p className="text-sm text-gray-600 italic">{engagement.note}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Important Notice */}
        <section className="py-12 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="bg-gradient-to-r from-[#1E3A5F] to-[#2d5a8f] rounded-2xl p-8 md:p-12 text-white">
                <h2 className="text-2xl font-bold mb-6 flex items-center gap-3">
                  <Package className="w-7 h-7 text-orange-400" />
                  Ce qu'il faut retenir
                </h2>
                
                <div className="grid md:grid-cols-2 gap-8">
                  <div>
                    <h3 className="font-semibold text-orange-400 mb-3 uppercase text-sm tracking-wider">
                      Livraison France entière
                    </h3>
                    <ul className="space-y-2 text-white/90">
                      <li className="flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4 text-green-400" />
                        Pièces détachées
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4 text-green-400" />
                        Accessoires spa
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4 text-green-400" />
                        Produits d'entretien
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4 text-green-400" />
                        Pompes à chaleur
                      </li>
                    </ul>
                  </div>
                  
                  <div>
                    <h3 className="font-semibold text-orange-400 mb-3 uppercase text-sm tracking-wider">
                      Zone locale (Grand Ouest)
                    </h3>
                    <ul className="space-y-2 text-white/90">
                      <li className="flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4 text-green-400" />
                        Installation de spas
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4 text-green-400" />
                        Mise en service
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4 text-green-400" />
                        Service après-vente
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4 text-green-400" />
                        Interventions techniques
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-12 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="max-w-2xl mx-auto text-center">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                Une question ? Besoin d'un conseil ?
              </h2>
              <p className="text-gray-600 mb-8">
                Notre équipe est disponible par email pour répondre à toutes vos questions 
                et vous accompagner dans votre projet.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button asChild size="lg" className="bg-[#1E3A5F] hover:bg-[#2d5a8f]">
                  <Link to="/contact">
                    <Mail className="w-5 h-5 mr-2" />
                    Nous contacter
                  </Link>
                </Button>
                <Button asChild variant="outline" size="lg">
                  <Link to="/faq">
                    Consulter la FAQ
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default GarantiesEngagementsPage;
